import type { AlvoDaIntimacao } from '../types/editor';

// A intimação da sentença vai às partes do litígio numa execução só (desenho de 18/08 com a
// Secretaria). Até 10/2026 o bloco escolhia UMA parte, e o fluxo publicado intimava só o primeiro
// requerido. Os requeridos e os terceiros interessados são sempre intimados; o requerente depende
// do check do bloco, que nasce marcado (o desenho é "todas as partes").

const CHAVE_DA_PARTE_UNICA = 'parteArbitralId';
const PARTE_UNICA_REQUERENTE = '{{requerente_id}}';
const PARTES_UNICAS_DO_COMPILADOR: ReadonlySet<unknown> = new Set([
  PARTE_UNICA_REQUERENTE,
  '{{requerido_id}}',
]);

/**
 * A parte única que o compilador emitia antes do check: `{{requerente_id}}` ou `{{requerido_id}}`.
 * Ele não a emite mais (o executor recusa a parte junto com o leque), então o save pela régua a
 * troca pelo leque de propósito, e a guarda do editor não acusa essa troca. Outro valor (um id
 * literal, gravado pelo editor de fluxos ou por migration) não volta para o check e cairia no
 * leque sem o requerente: esse a guarda acusa.
 */
export function ehParteUnicaDoCompilador(chave: string, valor: unknown): boolean {
  return chave === CHAVE_DA_PARTE_UNICA && PARTES_UNICAS_DO_COMPILADOR.has(valor);
}

/**
 * O check "Enviar também para o requerente". Bloco salvo antes do check continua alcançando quem
 * alcançava: o que mirava o requerente (`para: 'REQUERENTE'` no editor, a parte única
 * `{{requerente_id}}` no nó) e o leque de "todas as partes" (`para: 'TODOS'` no editor,
 * `intimarTodasAsPartes` sem o check no nó — que o executor lê como requerente incluído).
 */
export function intimaORequerente(parametros: Record<string, unknown>): boolean {
  if (typeof parametros.intimarRequerente === 'boolean') return parametros.intimarRequerente;
  return (
    parametros.para === 'REQUERENTE' ||
    parametros.para === 'TODOS' ||
    parametros.intimarTodasAsPartes === true ||
    parametros[CHAVE_DA_PARTE_UNICA] === PARTE_UNICA_REQUERENTE
  );
}

/**
 * O alvo que o executor `arbitral.intimar_sentenca` entende: sempre o leque, SEM `parteArbitralId`
 * (o executor recusa os dois juntos), e o requerente pelo check.
 *
 * @example compilarAlvoDaIntimacao({ intimarRequerente: true })
 * // { intimarTodasAsPartes: true, intimarRequerente: true }
 */
export function compilarAlvoDaIntimacao(parametros: Record<string, unknown>): AlvoDaIntimacao {
  return { intimarTodasAsPartes: true, intimarRequerente: intimaORequerente(parametros) };
}

/** "os requeridos" ou "requeridos e requerente", para o resumo do bloco no canvas. */
export function resumoDoAlvoDaIntimacao(parametros: Record<string, unknown>): string {
  return intimaORequerente(parametros) ? 'requeridos e requerente' : 'os requeridos';
}
